import classNames from "classnames";
import Cleave from "cleave.js/react";
import moment from "moment";
import React, { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CalendarForm } from "../../../../calendars.types";
import css from "../../dates.module.scss";
import { extractErrorMessage } from "../utils";
import { transform } from "./working_hours.utils";

type WorkingHoursProps = {
  index: number;
  daysType: "nominalDays" | "exceptionDays";
  isDisabled: boolean;
};

export const WorkingHours = ({ index, daysType, isDisabled }: WorkingHoursProps): JSX.Element => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext<CalendarForm>();
  const { t } = useTranslation("bookingModule");
  const [from, to, duration] = watch([
    `${daysType}.${index}.startTime`,
    `${daysType}.${index}.endTime`,
    `${daysType}.${index}.workingMinutes`
  ]);

  useEffect(() => {
    trigger([
      `${daysType}.${index}.startTime`,
      `${daysType}.${index}.endTime`,
      `${daysType}.${index}.workingMinutes`
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, duration]);

  const dateRangeExists = from && to;

  const maxDuration = dateRangeExists
    ? moment.duration(moment(to, "HH:mm").diff(moment(from, "HH:mm"))).asMinutes()
    : 0;

  const handleChange = useCallback(
    (event) => {
      const value = transform.output(event);
      if (value) {
        setValue(`${daysType}.${index}.workingMinutes`, value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [index]
  );
  const error = extractErrorMessage(index, errors, daysType);

  return (
    <Cleave
      className={classNames(css.workingHours, css.input, {
        [css.inputError]: Boolean(error),
        [css.disabled]: isDisabled
      })}
      {...register(`${daysType}.${index}.workingMinutes`, {
        max: {
          value: maxDuration,
          message: t("config.calendars.workingHours.error")
        },
        value: maxDuration
      })}
      options={{
        time: true,
        timePattern: ["h", "m"]
      }}
      placeholder="hh:mm"
      value={transform.input(duration ?? maxDuration)}
      onChange={handleChange}
      disabled={isDisabled}
    />
  );
};
